import { useState, type ReactElement } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Archive,
  Info,
  LogOut,
  Pencil,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import {
  ActionMenu,
  Alert,
  AsyncSection,
  asyncStatus,
  Badge,
  Breadcrumb,
  Button,
  ConfirmDialog,
  Skeleton,
  DataTable,
  SettingsGroup,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  StatusPill,
  TabPanel,
  Tabs,
  TruncatedText,
  toast,
  type DataTableColumn,
} from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import { canAdministerWorkspace } from "../../auth/roles.js";
import { TEAMS_ROUTE } from "../../routes.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import type {
  Team,
  TeamMember,
  TeamRequest,
  TeamRequestAction,
} from "../../api/teams-api.js";
import { LoadMore } from "./LoadMore.js";
import {
  AdminActionDialog,
  EditTeamDialog,
  InviteTeamDialog,
  type AdminAction,
} from "./TeamDialogs.js";
import { TeamRequestList } from "./TeamRequestList.js";
import {
  PersonInitials,
  TabCount,
  TeamTile,
  TeamViewerStatus,
  tabLabel,
} from "./team-identity.js";
import {
  useArchiveOwnerlessTeam,
  useArchiveTeam,
  useIncomingTeamRequests,
  useLeaveTeam,
  useRecoverTeamOwnership,
  useRemoveTeamMember,
  useRequestToJoinTeam,
  useResolveTeamRequest,
  useSetTeamMemberRole,
  useTeam,
  useTeamMembers,
} from "./useTeams.js";

/**
 * One team's profile and roster (ADR-0082), drawn as a Settings child page: a
 * breadcrumb back to Teams, the team's own header, then the Settings lane with
 * its meta aside.
 *
 * READABLE BY EVERY ACTIVE MEMBER of the workspace, including an archived
 * team's: teams are workspace-visible, and an archived team's history is
 * exactly what a closed campaign's provenance points at. What changes with the
 * reader is which ACTIONS appear, and every one of them is re-decided
 * server-side from the locked row — this page's `viewerRole` is presentation.
 *
 * ONE ACCENT IN THE HEADER (DS invariant §9), and it is the thing this reader
 * can commit here: an owner's **Invite people**, an outsider's **Ask to join**.
 * A member has nothing to commit, so their Leave is secondary. Everything an
 * owner does less often — edit details, leave, archive — sits in the header's
 * overflow menu, and leaving and archiving each ask first.
 *
 * EVERY OWNER MUTATION CARRIES `team.version`. A stale one comes back as a
 * refusal naming both numbers, which is rendered verbatim rather than
 * translated: "This team changed somewhere else. Reload it and try again" is
 * the actionable sentence, and a generic "Something went wrong" would throw it
 * away. Beside it is the action that sentence asks for — see `RefusalNotice`.
 *
 * THE OWNER'S QUEUE IS TWO QUEUES, and conflating them was a real defect. The
 * incoming-requests endpoint is scoped by OWNERSHIP, so it returns join
 * requests somebody sent AND invitations this owner sent out; rendering both as
 * "asked to join" with an Approve button described the opposite of what had
 * happened and offered a control the server refuses. `presentTeamRequest` is
 * the shared decision, and the two are separate sections of the Pending tab.
 */
export function TeamDetailPage(): ReactElement {
  const { teamId = "" } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const team = useTeam(teamId);
  const members = useTeamMembers(teamId);
  /* SERVER-SIDE, by team. The page used to read the caller's whole queue and
     filter it in the browser: that is every team they own rather than this one,
     and this team's requests could sit past the first page of that combined
     queue and never appear at all. */
  const requests = useIncomingTeamRequests({ teamId });

  const reload = (): void => {
    void team.refetch();
    members.refetch();
    requests.refetch();
  };

  return (
    <div className="pb-motion-content-enter">
      <Breadcrumb
        className="mb-4"
        items={[
          { label: "Teams", href: TEAMS_ROUTE },
          { label: team.data?.name ?? "Team" },
        ]}
        onNavigate={(href) => navigate(href)}
      />

      <AsyncSection
        status={asyncStatus({
          isLoading: team.isLoading,
          isError: team.isError,
          isEmpty: false,
          hasData: team.data !== undefined,
        })}
        errorTitle={"This team could not be loaded"}
        loadingLabel="the team"
        skeleton={
          <div className="mb-6 flex items-start gap-4 border-b border-pb-border/70 pb-4.5">
            <Skeleton shape="card" className="h-12 w-12" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-7 w-64" />
              <Skeleton className="h-4 w-96 max-w-full" />
            </div>
          </div>
        }
        errorDescription={
          team.error instanceof Error
            ? team.error.message
            : "It may have been removed, or it belongs to another workspace."
        }
        onRetry={reload}
      >
        {team.data && (
          <TeamDetail
            team={team.data}
            members={members.items}
            membersLoading={members.isLoading}
            membersError={members.error !== null}
            membersHasMore={members.hasMore}
            membersLoadingMore={members.isLoadingMore}
            membersLoadMoreFailed={members.loadMoreFailed}
            onLoadMoreMembers={members.loadMore}
            onRetryMembers={members.refetch}
            requests={requests.items}
            requestsLoading={requests.isLoading}
            requestsError={requests.error !== null}
            onRetryRequests={requests.refetch}
            requestsHasMore={requests.hasMore}
            requestsLoadingMore={requests.isLoadingMore}
            requestsLoadMoreFailed={requests.loadMoreFailed}
            onLoadMoreRequests={requests.loadMore}
            onReload={reload}
          />
        )}
      </AsyncSection>
    </div>
  );
}

type TeamTab = "members" | "pending";

/** How somebody came to be on the roster, in the reader's words. */
const JOINED_VIA: Record<TeamMember["joinedVia"], string> = {
  created: "Created the team",
  invitation: "Accepted an invitation",
  join_request: "Request approved",
  admin_recovery: "Ownership recovered by an admin",
  seed: "Added when the workspace was set up",
};

function TeamDetail({
  team,
  members,
  membersLoading,
  membersError,
  membersHasMore,
  membersLoadingMore,
  membersLoadMoreFailed,
  onLoadMoreMembers,
  onRetryMembers,
  requests,
  requestsLoading,
  requestsError,
  onRetryRequests,
  requestsHasMore,
  requestsLoadingMore,
  requestsLoadMoreFailed,
  onLoadMoreRequests,
  onReload,
}: {
  team: Team;
  members: readonly TeamMember[];
  membersLoading: boolean;
  membersError: boolean;
  membersHasMore: boolean;
  membersLoadingMore: boolean;
  membersLoadMoreFailed: boolean;
  onLoadMoreMembers: () => void;
  onRetryMembers: () => void;
  requests: readonly TeamRequest[];
  requestsLoading: boolean;
  requestsError: boolean;
  onRetryRequests: () => void;
  requestsHasMore: boolean;
  requestsLoadingMore: boolean;
  requestsLoadMoreFailed: boolean;
  onLoadMoreRequests: () => void;
  onReload: () => void;
}): ReactElement {
  const navigate = useNavigate();
  const format = useDateTimeFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const archived = team.archivedAt !== null;
  const isOwner = team.viewerRole === "owner" && !archived;
  const isMember = team.viewerRole === "member" && !archived;
  const isOutsider =
    !archived && team.viewerRole === null && team.viewerPendingKind === null;

  const claims = useAuthStore((state) => state.claims);
  const isWorkspaceAdmin = canAdministerWorkspace(claims);

  const setRole = useSetTeamMemberRole(team.teamId);
  const removeMember = useRemoveTeamMember(team.teamId);
  const archive = useArchiveTeam(team.teamId);
  const leave = useLeaveTeam();
  const join = useRequestToJoinTeam();
  const resolve = useResolveTeamRequest();
  const recover = useRecoverTeamOwnership(team.teamId);
  const adminArchive = useArchiveOwnerlessTeam(team.teamId);

  const [inviting, setInviting] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  /* `?action=edit` (Admin › Identity › Teams → Rename) opens Edit details on
     arrival, for an owner of an active team only — anybody else gets the page
     as it is. Cleared when the dialog closes, as `/teams?action=create` is. */
  const [editing, setEditing] = useState(
    () => isOwner && searchParams.get("action") === "edit",
  );
  const [adminAction, setAdminAction] = useState<AdminAction>(null);
  /* Set by anything that makes an older failure OBSOLETE but is not itself one
     of the mutations below — a reload, or the edit dialog's own success, which
     owns its mutation inside the dialog so it can be reset per opening. */
  const [dismissedAt, setDismissedAt] = useState(0);

  /* The Pending tab is an owner's; anybody else asking for it by URL gets the
     roster rather than an empty panel. */
  const tab: TeamTab =
    isOwner && searchParams.get("tab") === "pending" ? "pending" : "members";
  const showTab = (next: TeamTab): void => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "members") params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  };

  const joinRequests = requests.filter((row) => row.kind === "join_request");
  const sentInvitations = requests.filter((row) => row.kind === "invitation");
  const pendingCount = joinRequests.length + sentInvitations.length;

  /*
    THE MOST RECENT FAILURE, and only while it is still the most recent thing
    that happened.

    A TanStack mutation keeps `isError` until it is reset, so taking the first
    errored one left "That did not go through" on screen for ever: a refused
    last-owner leave stayed visible through a successful description edit, a
    successful role change and a reload, describing none of them. Comparing
    `submittedAt` is what makes the notice mean "the last thing you did was
    refused" rather than "something was refused at some point".

    The edit and invite dialogs' OWN errors are deliberately not in here — they
    render inside their dialogs, next to the fields they are about, and an
    active form error must survive anything happening behind it.
  */
  const mutations = [
    setRole,
    removeMember,
    archive,
    leave,
    join,
    resolve,
    recover,
    adminArchive,
  ];
  const latestSuccessAt = Math.max(
    dismissedAt,
    ...mutations.map((mutation) =>
      mutation.isSuccess ? mutation.submittedAt : 0,
    ),
  );
  const lastError = mutations
    .filter(
      (mutation) => mutation.isError && mutation.submittedAt > latestSuccessAt,
    )
    .sort((a, b) => b.submittedAt - a.submittedAt)
    .map((mutation) => mutation.error)
    .find((error): error is Error => error instanceof Error);

  const personLabel = (row: TeamMember): string =>
    row.displayName ?? row.primaryEmail ?? row.userId;

  const columns: ReadonlyArray<DataTableColumn<TeamMember>> = [
    {
      id: "person",
      header: "Person",
      cardRole: "title",
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-3">
          <PersonInitials
            name={row.displayName ?? row.primaryEmail}
            seed={row.userId}
          />
          <div className="min-w-0">
            <span className="block truncate font-medium text-pb-text">
              {row.displayName ?? "Name unavailable"}
            </span>
            {/* A clipped address shows in the design-system Tooltip (d1),
                not a native title. */}
            {row.primaryEmail && (
              <span className="block text-caption text-pb-text-muted">
                <TruncatedText>{row.primaryEmail}</TruncatedText>
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "role",
      header: "Role",
      width: "7rem",
      cardRole: "badge",
      /* The same type size either way: a bare "Member" in the cell's body size
         sat larger than the Owner badge beside it, most visibly in the card
         list on a phone. */
      cell: (row) =>
        row.role === "owner" ? (
          <Badge>Owner</Badge>
        ) : (
          <span className="text-body-small text-pb-text-secondary">Member</span>
        ),
    },
    {
      id: "joined",
      header: "Joined",
      width: "13rem",
      cell: (row) => (
        <div className="min-w-0">
          <span className="block tabular-nums text-pb-text-secondary">
            {format.date(row.joinedAt)}
          </span>
          <span className="block text-caption text-pb-text-muted">
            <TruncatedText>{JOINED_VIA[row.joinedVia]}</TruncatedText>
          </span>
        </div>
      ),
    },
    {
      id: "reachable",
      header: "Status",
      width: "11.5rem",
      cell: (row) =>
        row.deliverable ? (
          <span className="text-pb-text-muted">Reachable</span>
        ) : (
          /* SHOWN, not filtered: somebody who has left the workspace or been
             disabled is still on this roster, and an owner needs to see that
             they are there and unreachable — which is exactly the state a
             silent filter would hide until a campaign under-delivered. */
          <StatusPill tone="watch">No longer reachable</StatusPill>
        ),
    },
    ...(isOwner
      ? ([
          {
            id: "actions",
            header: <span className="sr-only">Actions</span>,
            width: "3.5rem",
            align: "end",
            cardRole: "actions",
            cell: (row: TeamMember) => (
              <ActionMenu
                triggerLabel={`Actions for ${personLabel(row)}`}
                triggerSize="sm"
                align="end"
                items={[
                  {
                    id: "role",
                    label: row.role === "owner" ? "Make member" : "Make owner",
                    ...(setRole.isPending
                      ? {
                          disabledReason:
                            "A role change is already being saved.",
                        }
                      : {}),
                    onSelect: () =>
                      setRole.mutate({
                        userId: row.userId,
                        role: row.role === "owner" ? "member" : "owner",
                        version: team.version,
                      }),
                  },
                  {
                    id: "remove",
                    label: "Remove from team",
                    tone: "critical",
                    separatorBefore: true,
                    ...(removeMember.isPending
                      ? { disabledReason: "A removal is already being saved." }
                      : {}),
                    onSelect: () =>
                      removeMember.mutate({
                        userId: row.userId,
                        version: team.version,
                      }),
                  },
                ]}
              />
            ),
          },
        ] as ReadonlyArray<DataTableColumn<TeamMember>>)
      : []),
  ];

  const onResolve = (requestId: string, action: TeamRequestAction): void =>
    resolve.mutate({ requestId, action });

  const leaveTeam = (): void =>
    leave.mutate(team.teamId, {
      onSuccess: () => {
        setConfirmLeave(false);
        toast(`You left ${team.name}.`, { tone: "healthy" });
        navigate(TEAMS_ROUTE);
      },
      /* Closed on a refusal too, so the refusal notice — the server's own
         sentence, "A team keeps at least one owner" — is not hidden behind the
         dialog that caused it. */
      onError: () => setConfirmLeave(false),
    });

  const meta = (
    /* The same stack the Account page uses for two meta cards: without
       it the cards sat edge to edge with no gap. */
    <div className="flex flex-col gap-3.5">
      <SettingsMetaCard
        icon={Info}
        title="About this team"
        description="Readable by every member of the workspace. Owners manage the roster; nobody gains access by being on it."
        rows={[
          {
            label: "Your role",
            value:
              team.viewerRole === "owner"
                ? "Owner"
                : team.viewerRole === "member"
                  ? "Member"
                  : "Not on this team",
          },
          { label: "Reachable members", value: team.memberCount },
          { label: "Owners", value: team.ownerCount },
          { label: "Created", value: format.date(team.createdAt) },
        ]}
      />
      {isWorkspaceAdmin && !archived && (
        <AdministratorActions
          team={team}
          onRecover={() => setAdminAction("recover")}
          onArchive={() => setAdminAction("archive")}
        />
      )}
    </div>
  );

  /* The header stays hand-built: SectionHeader puts `leading` inside the
     title row (gap-2, centred) with the description under the tile, where
     today the 48px tile sits beside the whole title block; no PR 3 look row
     approves that. */
  return (
    <>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-pb-border/70 pb-4.5">
        <div className="flex min-w-0 items-start gap-4">
          <TeamTile name={team.name} size="lg" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-heading-1 tracking-tight text-pb-text">
                {team.name}
              </h1>
              <TeamViewerStatus team={team} quietWhenNone />
            </div>
            {/* No counts line here: reachable members, owners and the
                created date are the About card's rows, and saying them twice
                on one screen is noise. */}
            {team.description && (
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-pb-text-secondary">
                {team.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SettingsDetailsButton meta={meta} />
          {isOwner && (
            <>
              <Button variant="primary" onClick={() => setInviting(true)}>
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Invite people
              </Button>
              <ActionMenu
                triggerLabel="More team actions"
                align="end"
                items={[
                  {
                    id: "edit",
                    label: "Edit details",
                    icon: Pencil,
                    onSelect: () => setEditing(true),
                  },
                  {
                    id: "leave",
                    label: "Leave team",
                    icon: LogOut,
                    onSelect: () => setConfirmLeave(true),
                  },
                  {
                    id: "archive",
                    label: "Archive team",
                    icon: Archive,
                    tone: "critical",
                    separatorBefore: true,
                    onSelect: () => setConfirmArchive(true),
                  },
                ]}
              />
            </>
          )}
          {isMember && (
            <Button variant="secondary" onClick={() => setConfirmLeave(true)}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Leave team
            </Button>
          )}
          {isOutsider && (
            <Button
              variant="primary"
              disabled={join.isPending}
              onClick={() => join.mutate(team.teamId)}
            >
              Ask to join
            </Button>
          )}
          {/* Answering it is the Requests view's job, so this is a way there
              rather than a second Accept that could drift from the first. */}
          {!archived && team.viewerPendingKind === "invitation" && (
            <Button
              variant="secondary"
              onClick={() => navigate(`${TEAMS_ROUTE}?view=requests`)}
            >
              Answer invitation
            </Button>
          )}
        </div>
      </header>

      <SettingsPageLayout mainWidth="fluid" meta={meta}>
        {archived && (
          <Alert
            role="status"
            tone="info"
            title="This team is archived"
            className="mb-4"
          >
            Its roster stays readable — work already sent through it still names
            these people. It cannot be joined, invited to, or changed, and there
            is no way to un-archive it.
          </Alert>
        )}

        {lastError && (
          <div className="mb-4">
            <RefusalNotice
              error={lastError}
              onReload={() => {
                /* Re-reading answers the question the notice was asking, so
                   the notice goes with it rather than outliving its own
                   remedy. */
                setDismissedAt(Date.now());
                onReload();
              }}
            />
          </div>
        )}

        <Tabs
          aria-label="Team sections"
          items={[
            { value: "members", label: "Members" },
            ...(isOwner
              ? [
                  {
                    value: "pending",
                    label: tabLabel(
                      "Pending",
                      pendingCount > 0
                        ? `${pendingCount}${requestsHasMore ? "+" : ""}`
                        : undefined,
                      joinRequests.length > 0,
                    ),
                  },
                ]
              : []),
          ]}
          value={tab}
          onValueChange={(next) =>
            showTab(next === "pending" ? "pending" : "members")
          }
        >
          <TabPanel value={tab} className="pt-4.5">
            {tab === "members" ? (
              <div className="space-y-3">
                <DataTable
                  columns={columns}
                  data={[...members]}
                  getRowId={(row) => row.userId}
                  layout="fixed"
                  responsive
                  loading={membersLoading}
                  error={
                    membersError
                      ? {
                          title: "The roster could not be loaded",
                          description: "Try again in a moment.",
                          onRetry: onRetryMembers,
                        }
                      : undefined
                  }
                  caption="Accepted members only. Somebody with a pending invitation is not on this list."
                  emptyState={
                    /* Kept as today's plain line: EmptyState draws a
                       medallion panel (sm: 11px type), a look change no PR 3
                       row approves. */
                    <p className="text-pb-text-muted">
                      Nobody has accepted yet — an invitation is not membership.
                    </p>
                  }
                />
                {/* A ROSTER IS BOUNDED AT 1000 AND SERVED 100 AT A TIME, so a
                    large team has real pages — and the per-member owner
                    controls live on the rows, which means an unpaged roster
                    hides the controls too, not just the names. */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* TWO FACTS, NOT A FRACTION. The roster lists EVERY
                      accepted row — an inactive or disabled member still holds
                      their seat and their owner controls — while `memberCount`
                      counts only who can actually be reached. Rendered as "N of
                      M shown" the numerator could exceed the denominator, and
                      "12 of 11 shown" is the UI asserting a containment that
                      does not hold. */}
                  <p className="text-body-small tabular-nums text-pb-text-muted">
                    {`${members.length} shown${membersHasMore ? " so far" : ""} · ${team.memberCount} reachable`}
                  </p>
                  <LoadMore
                    label="members"
                    hasMore={membersHasMore}
                    isLoadingMore={membersLoadingMore}
                    failed={membersLoadMoreFailed}
                    onLoadMore={onLoadMoreMembers}
                  />
                </div>
              </div>
            ) : (
              /* The queue owns its four states (packages/ui §8). Before this,
                 a queue still loading, or one that failed, rendered both
                 sections' EMPTY copy — "Nobody is asking to join" is a claim
                 about the team, and neither a pending nor a failed read
                 supports it. A failed Load more keeps the rows on screen. */
              <AsyncSection
                status={asyncStatus({
                  isLoading: requestsLoading,
                  isError: requestsError,
                  isEmpty: false,
                  hasData:
                    requests.length > 0 || (!requestsLoading && !requestsError),
                })}
                loadingLabel="this team's requests"
                errorTitle="This team's requests could not be loaded"
                errorDescription="Invitations and requests to join are temporarily unavailable."
                onRetry={onRetryRequests}
                skeleton={
                  /* Kept hand-built: Card is the 14px corner with an 80%
                     border and the top hairline, where this placeholder is
                     10px on today's full pb-border. */
                  <div className="space-y-3 rounded-lg border border-pb-border bg-pb-card p-4">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-4/5" />
                  </div>
                }
              >
                <div className="space-y-7.5">
                  <SettingsGroup
                    variant="settings"
                    title="Requests to join"
                    description="Somebody asked to be on this team. Approving adds them; declining does not."
                    badge={
                      joinRequests.length > 0 ? (
                        <TabCount value={joinRequests.length} attention />
                      ) : null
                    }
                  >
                    <div className="mt-3">
                      <TeamRequestList
                        rows={joinRequests.map((row) => ({
                          queue: "incoming" as const,
                          row,
                        }))}
                        pending={resolve.isPending}
                        onResolve={onResolve}
                        showTeamLink={false}
                        /* One cursor holds BOTH kinds, so a first page of only
                       invitations says nothing about join requests on page
                       two. */
                        emptyMessage={
                          requestsHasMore
                            ? "Nobody on the pages loaded so far — use Load more below to see the rest."
                            : "Nobody is asking to join."
                        }
                      />
                    </div>
                  </SettingsGroup>
                  {/* THE OTHER DIRECTION, and a different heading rather than a
                    different row in the same list: these are the owner's own
                    invitations, nobody has accepted them yet, and the only
                    thing the owner can do is take one back. */}
                  <SettingsGroup
                    variant="settings"
                    title="Invitations you sent"
                    description="They are not on the team until they accept. You can withdraw an invitation; only the invited person can answer it."
                    badge={
                      sentInvitations.length > 0 ? (
                        <TabCount value={sentInvitations.length} />
                      ) : null
                    }
                  >
                    <div className="mt-3">
                      <TeamRequestList
                        rows={sentInvitations.map((row) => ({
                          queue: "incoming" as const,
                          row,
                        }))}
                        pending={resolve.isPending}
                        onResolve={onResolve}
                        showTeamLink={false}
                        emptyMessage={
                          requestsHasMore
                            ? "None on the pages loaded so far — use Load more below to see the rest."
                            : "No invitation is waiting for an answer."
                        }
                      />
                    </div>
                  </SettingsGroup>
                  <LoadMore
                    label="outstanding requests"
                    hasMore={requestsHasMore}
                    isLoadingMore={requestsLoadingMore}
                    failed={requestsLoadMoreFailed}
                    onLoadMore={onLoadMoreRequests}
                  />
                </div>
              </AsyncSection>
            )}
          </TabPanel>
        </Tabs>
      </SettingsPageLayout>

      <AdminActionDialog
        /* One mount per opening, like the other dialogs. A successful
           recovery closes through `setAdminAction(null)`, which never ran the
           dialog's own reset, so the next opening kept the last owner and
           audit reason. */
        key={adminAction ?? "closed"}
        action={adminAction}
        team={team}
        members={members}
        membersHasMore={membersHasMore}
        onClose={() => setAdminAction(null)}
        onRecover={(input) =>
          recover.mutate(input, { onSuccess: () => setAdminAction(null) })
        }
        onArchive={(input) =>
          adminArchive.mutate(input, { onSuccess: () => setAdminAction(null) })
        }
        pending={recover.isPending || adminArchive.isPending}
      />

      <InviteTeamDialog
        /* One mount per opening: a fresh open is an empty picker and a fresh
           mutation state, so a refused invitation does not greet the next
           one. */
        key={inviting ? "invite-open" : "invite-closed"}
        open={inviting}
        team={team}
        onClose={() => setInviting(false)}
      />

      <EditTeamDialog
        /* One mount per OPENING, and deliberately not per version.
           `editing` alone already forces a fresh mount (and a fresh mutation
           state) every time the dialog opens, which is the guarantee the
           component's own comment relies on — so the shut branch re-seeds from
           the current team whenever it is next opened.

           The version is NOT in the key, because it moves WHILE the dialog is
           open: a save that loses a version race tells the user to reload, and
           the reload brings back a newer `team.version`. Keying on it remounted
           the dialog mid-edit and replaced the name and description they had
           just typed with the server's. The dialog now stays mounted across
           that reload, takes the refreshed version as a prop, and the retry
           saves the user's own draft against it. */
        key={editing ? "edit-open" : "edit-closed"}
        open={editing}
        team={team}
        onClose={() => {
          setEditing(false);
          if (searchParams.get("action") === "edit") {
            setSearchParams(
              (prev) => {
                const params = new URLSearchParams(prev);
                params.delete("action");
                return params;
              },
              { replace: true },
            );
          }
        }}
        onSaved={() => setDismissedAt(Date.now())}
        onReload={onReload}
      />

      <ConfirmDialog
        open={confirmLeave}
        title={`Leave ${team.name}?`}
        confirmLabel="Leave team"
        confirmDisabled={leave.isPending}
        cancelDisabled={leave.isPending}
        onClose={() => setConfirmLeave(false)}
        onConfirm={leaveTeam}
      >
        You come off this team&apos;s roster. To come back, you ask to join
        again. A team always keeps at least one owner.
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmArchive}
        title={`Archive ${team.name}?`}
        confirmLabel="Archive team"
        confirmDisabled={archive.isPending}
        cancelDisabled={archive.isPending}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() =>
          archive.mutate(team.version, {
            onSuccess: () => setConfirmArchive(false),
            onError: () => setConfirmArchive(false),
          })
        }
      >
        Its roster and history stay readable, which is why the team is kept
        rather than deleted. Outstanding invitations and requests are cancelled,
        nothing new can target it, and there is no way to un-archive it.
      </ConfirmDialog>
    </>
  );
}

/**
 * A refused owner mutation, with the action the server's own sentence asks for.
 *
 * THE MESSAGE IS THE SERVER'S, VERBATIM — "This team changed somewhere else.
 * Reload it and try again (you had version 3, it is now 4)" names both numbers
 * and what to do. The Reload button is beside it because a message telling
 * somebody to reload, on a screen with no way to reload, is half an answer.
 *
 * Offered on EVERY refusal rather than only on a version conflict. The version
 * conflict is a plain 409 with no distinct code, so discriminating would mean
 * matching on the message text — and re-reading is a reasonable response to any
 * refusal here anyway: it is the screen catching up with a server that already
 * disagreed with it.
 */
function RefusalNotice({
  error,
  onReload,
}: {
  error: Error;
  onReload: () => void;
}): ReactElement {
  return (
    <Alert role="status" tone="critical" title="That did not go through">
      <div className="space-y-2">
        <p>{error.message}</p>
        <Button variant="secondary" size="sm" onClick={onReload}>
          Reload this team
        </Button>
      </div>
    </Alert>
  );
}

/**
 * The two ADMINISTRATOR powers over a team (ADR-0082 rule 7), in the team
 * page's meta aside rather than in the Admin console.
 *
 * HERE BECAUSE THE CONTEXT IS HERE: the roster this recovery promotes from, the
 * owner count that decides whether archiving is even offered, and the team's
 * name are all already on screen. A console section would have to re-fetch all
 * three and would put an act about ONE team behind a workspace-wide rail.
 *
 * NEITHER ADDS ANYBODY. Recovery promotes somebody already on the team —
 * consent is not an administrator's to override, and the server refuses a
 * target who is not already a member. Archiving retires a team nobody is left
 * to own; it is offered ONLY when `ownerCount` is zero, which is the same
 * condition the server checks under the team row lock (`ownerCount` counts
 * owners who can still ACT, so an offboarded owner does not hide this).
 *
 * IT GATES NOTHING. `canAdministerWorkspace` hides the card; the server decides.
 * Its buttons are secondary: they OPEN a dialog, whose confirm carries the
 * accent.
 */
function AdministratorActions({
  team,
  onRecover,
  onArchive,
}: {
  team: Team;
  onRecover: () => void;
  onArchive: () => void;
}): ReactElement {
  const ownerless = team.ownerCount === 0;
  return (
    <SettingsMetaCard
      icon={ShieldCheck}
      title="Admin actions"
      description={
        ownerless
          ? "This team has no owner who can still act on it. Recover ownership to somebody already on it, or archive it if nobody is left."
          : "For a team whose owners have left the workspace. Recovery promotes somebody already on the team — it never adds anybody, and it changes nobody's access."
      }
      action={
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={onRecover}>
            Recover ownership
          </Button>
          {/* Offered only in the state the server accepts it in, rather than
              disabled-with-a-reason: an administrator archiving a team that
              still has an owner is not a thing to explain, it is a thing the
              owners do themselves. */}
          {ownerless && (
            <Button variant="secondary" size="sm" onClick={onArchive}>
              Archive this ownerless team
            </Button>
          )}
        </div>
      }
    />
  );
}
